import { useState, useEffect, useRef, useCallback } from 'react';
import {
  FolderOpen, FileText, Upload, Download, Trash2, MoveRight, Plus,
  ChevronRight, ArrowLeft, Search, Loader2, Eye, Edit2, File, ListTodo, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { holePreviewUrl } from '@/lib/downloadTicket';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const ORDNER_ICONS = {
  aktenuebernahme: '📥',
  jobcenter: '🏢',
  rente_drv: '🏛️',
  krankenversicherung: '🏥',
  gericht_betreuung: '⚖️',
  wohnen: '🏠',
  schulden_glaeubiger: '💰',
  gesundheit: '❤️',
  sonstiges: '📁',
};

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function DokumentePhysisch({ klientId }) {
  const [ordnerListe, setOrdnerListe] = useState([]);
  const [selectedOrdner, setSelectedOrdner] = useState(null);
  const [dateien, setDateien] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moveDialog, setMoveDialog] = useState({ open: false, datei: null });
  const [moveTarget, setMoveTarget] = useState('');
  const [neuerOrdnerDialog, setNeuerOrdnerDialog] = useState(false);
  const [neuerOrdnerName, setNeuerOrdnerName] = useState('');
  const [renameDialog, setRenameDialog] = useState({ open: false, datei: null });
  const [neuerDateiName, setNeuerDateiName] = useState('');
  const [previewFile, setPreviewFile] = useState(null); // { url, name }
  const [dateiAufgaben, setDateiAufgaben] = useState({});
  const fileRef = useRef();

  const token = localStorage.getItem('olympus_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const loadOrdner = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/akten-ordner/liste/${klientId}`, { headers });
      const data = await res.json();
      setOrdnerListe(data.ordner || []);
    } catch (e) {
      toast.error('Fehler beim Laden der Ordner');
    }
    setLoading(false);
  }, [klientId]); // eslint-disable-line

  useEffect(() => { loadOrdner(); }, [loadOrdner]);

  function openOrdner(ordnerName) {
    const ordner = ordnerListe.find(o => o.name === ordnerName);
    setSelectedOrdner(ordnerName);
    setDateien(ordner?.dateien || []);
    // Aufgaben-Lookup für jede Datei
    (ordner?.dateien || []).forEach(async (d) => {
      try {
        const res = await fetch(
          `${API}/api/betreuung/datei-aufgaben/${klientId}/${ordnerName}/${encodeURIComponent(d.name)}`,
          { headers }
        );
        const aufgaben = await res.json();
        if (aufgaben.length > 0) {
          setDateiAufgaben(prev => ({ ...prev, [`${ordnerName}/${d.name}`]: aufgaben }));
        }
      } catch { /* ignore */ }
    });
  }

  function goBack() {
    setSelectedOrdner(null);
    setDateien([]);
  }

  async function uploadDateien(fileList) {
    if (!selectedOrdner) {
      toast.error('Bitte zuerst einen Ordner oeffnen');
      return;
    }
    const formData = new FormData();
    Array.from(fileList).forEach(f => formData.append('dateien', f));

    try {
      const res = await fetch(`${API}/api/akten-ordner/upload/${klientId}/${selectedOrdner}`, {
        method: 'POST', headers, body: formData
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`${data.gespeichert.length} Datei(en) hochgeladen`);
        await loadOrdner();
        openOrdner(selectedOrdner);
      }
    } catch (e) {
      toast.error('Upload fehlgeschlagen');
    }
  }

  async function deleteDatei(dateiName) {
    if (!window.confirm(`"${dateiName}" wirklich loeschen?`)) return;
    try {
      const res = await fetch(`${API}/api/akten-ordner/datei/${klientId}/${selectedOrdner}/${encodeURIComponent(dateiName)}`, {
        method: 'DELETE', headers
      });
      if (res.ok) {
        toast.success('Datei geloescht');
        await loadOrdner();
        const updated = ordnerListe.find(o => o.name === selectedOrdner);
        setDateien(updated?.dateien?.filter(d => d.name !== dateiName) || []);
      }
    } catch (e) {
      toast.error('Fehler beim Loeschen');
    }
  }

  async function verschiebenDatei() {
    if (!moveDialog.datei || !moveTarget) return;
    const formData = new FormData();
    formData.append('dateiname', moveDialog.datei);
    formData.append('von_ordner', selectedOrdner);
    formData.append('nach_ordner', moveTarget);

    try {
      const res = await fetch(`${API}/api/akten-ordner/verschieben/${klientId}`, {
        method: 'POST', headers, body: formData
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Verschoben nach ${moveTarget}`);
        setMoveDialog({ open: false, datei: null });
        await loadOrdner();
        openOrdner(selectedOrdner);
      }
    } catch (e) {
      toast.error('Fehler beim Verschieben');
    }
  }

  async function erstelleOrdner() {
    if (!neuerOrdnerName.trim()) return;
    const formData = new FormData();
    formData.append('name', neuerOrdnerName.trim());

    try {
      const res = await fetch(`${API}/api/akten-ordner/ordner-erstellen/${klientId}`, {
        method: 'POST', headers, body: formData
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Ordner "${neuerOrdnerName}" erstellt`);
        setNeuerOrdnerDialog(false);
        setNeuerOrdnerName('');
        await loadOrdner();
      }
    } catch (e) {
      toast.error('Fehler beim Erstellen');
    }
  }

  async function umbenenneDatei() {
    if (!renameDialog.datei || !neuerDateiName.trim()) return;
    const formData = new FormData();
    formData.append('neuer_name', neuerDateiName.trim());

    try {
      const res = await fetch(`${API}/api/akten-ordner/umbenennen/${klientId}/${selectedOrdner}/${encodeURIComponent(renameDialog.datei)}`, {
        method: 'POST', headers, body: formData
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Umbenannt');
        setRenameDialog({ open: false, datei: null });
        await loadOrdner();
        openOrdner(selectedOrdner);
      }
    } catch (e) {
      toast.error('Fehler beim Umbenennen');
    }
  }

  async function holeAktenDateiPreviewUrl(dateiName) {
    return holePreviewUrl(
      `${API}/api/akten-ordner/download-ticket/${klientId}/${selectedOrdner}/${encodeURIComponent(dateiName)}`,
      `${API}/api/akten-ordner/download/${klientId}/${selectedOrdner}/${encodeURIComponent(dateiName)}`,
      headers
    );
  }

  async function downloadDatei(dateiName) {
    try {
      const url = await holeAktenDateiPreviewUrl(dateiName);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Download nicht möglich');
    }
  }

  if (loading && ordnerListe.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dokumente-physisch">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {selectedOrdner && (
            <Button variant="ghost" size="sm" onClick={goBack} className="text-slate-600">
              <ArrowLeft className="w-4 h-4 mr-1" /> Zurueck
            </Button>
          )}
          <h3 className="text-sm font-medium text-slate-500">
            {selectedOrdner ? (
              <span className="flex items-center gap-1.5">
                <FolderOpen className="w-4 h-4" />
                {ordnerListe.find(o => o.name === selectedOrdner)?.label || selectedOrdner}
              </span>
            ) : 'Dokumentenordner'}
          </h3>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setNeuerOrdnerDialog(true)} data-testid="neuer-ordner-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Ordner
          </Button>
          {selectedOrdner && (
            <Button size="sm" onClick={() => fileRef.current?.click()} data-testid="upload-btn">
              <Upload className="w-3.5 h-3.5 mr-1" /> Hochladen
              <input ref={fileRef} type="file" multiple className="hidden" onChange={e => uploadDateien(e.target.files)} />
            </Button>
          )}
        </div>
      </div>

      {/* Ordner-Grid */}
      {!selectedOrdner && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {ordnerListe.map(o => (
            <button
              key={o.name}
              data-testid={`ordner-${o.name}`}
              onClick={() => openOrdner(o.name)}
              className="flex flex-col items-center p-4 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all group"
            >
              <span className="text-3xl mb-2">{ORDNER_ICONS[o.name] || '📂'}</span>
              <span className="text-sm font-medium text-slate-700 text-center">{o.label}</span>
              <span className="text-xs text-slate-400 mt-1">
                {o.dateien_anzahl} {o.dateien_anzahl === 1 ? 'Datei' : 'Dateien'}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Datei-Liste */}
      {selectedOrdner && (
        <Card>
          <CardContent className="p-0">
            {dateien.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm">
                <FolderOpen className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                Ordner ist leer
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {dateien.map((d, i) => {
                  const linkedAufgaben = dateiAufgaben[`${selectedOrdner}/${d.name}`] || [];
                  const oeffneVorschau = async () => {
                    try {
                      const url = await holeAktenDateiPreviewUrl(d.name);
                      setPreviewFile({ url, name: d.name });
                    } catch (e) {
                      toast.error('Vorschau konnte nicht geladen werden');
                    }
                  };
                  return (
                  <div
                    key={i}
                    className="px-4 py-3 hover:bg-slate-50 group"
                    data-testid={`datei-${d.name}`}
                  >
                    <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={oeffneVorschau}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer"
                      title="Vorschau öffnen"
                      data-testid={`datei-zeile-${d.name}`}
                    >
                      <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate group-hover:text-violet-700">{d.name}</div>
                        <div className="text-xs text-slate-400">{formatSize(d.groesse)} - {formatDate(d.geaendert)}</div>
                      </div>
                    </button>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={oeffneVorschau}
                        className="p-1.5 rounded hover:bg-violet-100 text-slate-400 hover:text-violet-600"
                        title="Vorschau"
                        data-testid={`datei-preview-${d.name}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => downloadDatei(d.name)}
                        className="p-1.5 rounded hover:bg-blue-100 text-slate-400 hover:text-blue-600"
                        title="Herunterladen"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => { setRenameDialog({ open: true, datei: d.name }); setNeuerDateiName(d.name); }}
                        className="p-1.5 rounded hover:bg-blue-100 text-slate-400 hover:text-blue-600"
                        title="Umbenennen"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setMoveDialog({ open: true, datei: d.name })}
                        className="p-1.5 rounded hover:bg-amber-100 text-slate-400 hover:text-amber-600"
                        title="Verschieben"
                      >
                        <MoveRight className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteDatei(d.name)}
                        className="p-1.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600"
                        title="Loeschen"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    </div>
                    {/* Verknüpfte Aufgaben unter der Datei */}
                    {linkedAufgaben.length > 0 && (
                      <div className="ml-8 mt-1 mb-1 flex flex-wrap gap-1.5">
                        {linkedAufgaben.map((a, j) => (
                          <span
                            key={j}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 text-violet-700 text-xs rounded-full border border-violet-200"
                          >
                            <ListTodo className="w-3 h-3" />
                            {a.titel}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Verschieben-Dialog */}
      <Dialog open={moveDialog.open} onOpenChange={(open) => !open && setMoveDialog({ open: false, datei: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>"{moveDialog.datei}" verschieben</DialogTitle>
          </DialogHeader>
          <Select value={moveTarget} onValueChange={setMoveTarget}>
            <SelectTrigger><SelectValue placeholder="Zielordner waehlen" /></SelectTrigger>
            <SelectContent>
              {ordnerListe.filter(o => o.name !== selectedOrdner).map(o => (
                <SelectItem key={o.name} value={o.name}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialog({ open: false, datei: null })}>Abbrechen</Button>
            <Button onClick={verschiebenDatei} disabled={!moveTarget}>Verschieben</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Neuer Ordner Dialog */}
      <Dialog open={neuerOrdnerDialog} onOpenChange={setNeuerOrdnerDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Neuen Ordner erstellen</DialogTitle></DialogHeader>
          <Input
            value={neuerOrdnerName}
            onChange={e => setNeuerOrdnerName(e.target.value)}
            placeholder="Ordnername"
            onKeyDown={e => e.key === 'Enter' && erstelleOrdner()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNeuerOrdnerDialog(false)}>Abbrechen</Button>
            <Button onClick={erstelleOrdner} disabled={!neuerOrdnerName.trim()}>Erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Umbenennen-Dialog */}
      <Dialog open={renameDialog.open} onOpenChange={(open) => !open && setRenameDialog({ open: false, datei: null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Datei umbenennen</DialogTitle></DialogHeader>
          <Input
            value={neuerDateiName}
            onChange={e => setNeuerDateiName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && umbenenneDatei()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog({ open: false, datei: null })}>Abbrechen</Button>
            <Button onClick={umbenenneDatei} disabled={!neuerDateiName.trim()}>Umbenennen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vorschau-Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="datei-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setPreviewFile(null); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-violet-500 shrink-0" />
                <p className="text-sm font-medium text-slate-800 truncate">{previewFile.name}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => window.open(previewFile.url, '_blank')}>
                  <Download className="w-4 h-4 mr-1" /> Herunterladen
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setPreviewFile(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              {previewFile.name.toLowerCase().endsWith('.pdf') ? (
                <iframe src={previewFile.url} className="w-full h-full border-0" title={previewFile.name} />
              ) : previewFile.name.match(/\.(jpe?g|png|gif|webp)$/i) ? (
                <div className="w-full h-full flex items-center justify-center bg-slate-100 p-4">
                  <img src={previewFile.url} alt={previewFile.name} className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-50">
                  <div className="text-center space-y-3">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto" />
                    <p className="text-sm text-slate-500">Für diesen Dateityp ist keine Vorschau verfügbar.</p>
                    <Button variant="outline" onClick={() => window.open(previewFile.url, '_blank')}>
                      <Download className="w-4 h-4 mr-1" /> Herunterladen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
